import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MDBContainer, MDBSpinner } from "mdb-react-ui-kit";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Download } from "lucide-react";
import { DataTable } from "../ui";
import AccountSection from "./AccountSection";
import AccountKpiCard from "./AccountKpiCard";
import SARIcon from "../SARIcon";
import {
  fetchAccountSummary,
  fetchSubscriptions,
  fetchBundleSubscription,
  fetchSubscriptionPayments,
  fetchBillingInvoices,
  downloadInvoice,
} from "../../services/accountService";
import BusinessProfileForm from "./BusinessProfileForm";
import { usePermission } from "../../hooks/usePermission";
import { PERMISSION_ACTIONS, RBAC_MODULES } from "../../config/routePermissions";
import "./MyAccountPage.css";

function formatDate(value, locale) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString(locale || undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return String(value);
  }
}

function paymentStatusClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "paid" || s === "active") return "my-account-status my-account-status--success";
  if (s === "upcoming" || s === "overdue" || s === "pending") return "my-account-status my-account-status--warning";
  return "my-account-status my-account-status--neutral";
}

function subscriptionStatusClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active" || s === "in trial") return "my-account-status my-account-status--success";
  if (s === "pending" || s === "suspended") return "my-account-status my-account-status--warning";
  return "my-account-status my-account-status--neutral";
}

function localizePaymentStatus(status, ta) {
  const s = String(status || "");
  if (s === "Paid") return ta("mockData.statuses.paid");
  if (s === "Upcoming") return ta("mockData.statuses.upcoming");
  if (s === "Overdue") return ta("mockData.statuses.overdue");
  if (s === "Cancelled") return ta("mockData.statuses.cancelled");
  return s;
}

function localizeSubscriptionStatus(status, ta) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return ta("mockData.statuses.active");
  if (s === "in trial") return ta("mockData.statuses.inTrial");
  if (s === "pending") return ta("mockData.statuses.pending");
  if (s === "suspended") return ta("mockData.statuses.suspended");
  if (s === "cancelled") return ta("mockData.statuses.cancelled");
  return status;
}

function MyAccountPage() {
  const { t, i18n } = useTranslation("translation");
  const { can } = usePermission();
  const canViewSubscriptions = can(RBAC_MODULES.SUBSCRIPTIONS, PERMISSION_ACTIONS.VIEW_SUBSCRIPTION);
  const canPerformSubscription = can(RBAC_MODULES.SUBSCRIPTIONS, PERMISSION_ACTIONS.PERFORM_SUBSCRIPTION);
  const ta = useCallback((key, opts) => t(`myAccount.${key}`, opts), [t]);
  const na = ta("na");

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [bundleItems, setBundleItems] = useState([]);
  const [bundleMeta, setBundleMeta] = useState(null);
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [unpaidCount, setUnpaidCount] = useState(0);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const [summaryRes, subsRes, paymentsRes, bundleRes, invoicesRes] = await Promise.all([
          fetchAccountSummary(),
          canViewSubscriptions
            ? fetchSubscriptions().catch(() => ({ subscriptions: [], count: 0 }))
            : Promise.resolve({ subscriptions: [], count: 0 }),
          canViewSubscriptions
            ? fetchSubscriptionPayments().catch(() => ({ payments: [], unpaidCount: 0 }))
            : Promise.resolve({ payments: [], unpaidCount: 0 }),
          canViewSubscriptions
            ? fetchBundleSubscription().catch(() => null)
            : Promise.resolve(null),
          canViewSubscriptions
            ? fetchBillingInvoices().catch(() => ({ invoices: [] }))
            : Promise.resolve({ invoices: [] }),
        ]);
        if (!cancelled) {
          setSummary(summaryRes);
          const bundle = bundleRes?.subscription;
          const bundleRows = (bundle?.items || []).map((item) => ({
            id: item.itemId || item.planCode,
            name: item.planName || item.planCode,
            type: "Subscription",
            plan: `${item.quantity} branches`,
            status: bundle.status,
            renewsAt: bundle.scheduledChange?.effectiveAt || bundle.renewsAt,
            startDate: bundle.renewsAt,
          }));
          setBundleItems(bundle?.items || []);
          setBundleMeta(bundle || null);
          setSubscriptions(bundleRows.length ? bundleRows : (subsRes.subscriptions || []));
          setPayments(paymentsRes.payments || []);
          setInvoices(invoicesRes.invoices || []);
          setUnpaidCount(paymentsRes.unpaidCount || 0);
        }
      } catch (err) {
        console.error("[MyAccountPage]", err);
        if (!cancelled) {
          toast.error(ta("loadError"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [canViewSubscriptions, ta]);

  const subtitle = useMemo(() => {
    if (!summary) return "";
    const name = summary.legalTradeName || summary.username || "";
    const branches = summary.branchCount ?? 0;
    return t("myAccount.subtitle", { name, count: branches });
  }, [summary, t]);

  const handleTaxDetailsSaved = (businessInfo) => {
    setSummary((prev) => (prev ? { ...prev, businessInfo } : prev));
  };

  const handleProfileSubmitted = async (data) => {
    setSummary((prev) => ({
      ...(prev || {}),
      legalTradeName: data?.legalTradeName ?? prev?.legalTradeName,
      servicesUnlocked: data?.servicesUnlocked,
      businessInfoComplete: data?.businessInfoComplete,
      businessInfoChangeRequestPending: Boolean(data?.businessInfoChangeRequestPending),
      businessProfile: data?.businessProfile || prev?.businessProfile,
    }));
    try {
      const fresh = await fetchAccountSummary();
      setSummary(fresh);
    } catch (err) {
      console.error("[MyAccountPage.refreshAfterProfileSubmit]", err);
    }
  };

  const handleDownloadInvoice = useCallback(async (paymentId) => {
    setDownloadingId(paymentId);
    try {
      const response = await downloadInvoice(paymentId, i18n.language);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const disposition = response.headers?.["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/i);
      link.download = match?.[1] || `invoice-${paymentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("[MyAccountPage.downloadInvoice]", err);
      toast.error(ta("downloadInvoiceError", { defaultValue: "Failed to download invoice." }));
    } finally {
      setDownloadingId(null);
    }
  }, [ta, i18n.language]);

  const tableProps = {
    hideFooter: true,
    hideToolbar: true,
    showColumnSettings: false,
    className: "my-account-table",
    maxBodyHeight: "none",
  };

  const emptySubscriptionsMsg = ta("empty.subscriptions");
  const emptyPaymentsMsg = ta("empty.payments");
  const emptyTicketsMsg = ta("empty.tickets");
  const emptyRow = useMemo(() => [{ id: "empty", _empty: true }], []);

  const subscriptionRows = useMemo(() => {
    if (!subscriptions.length) return emptyRow;
    return subscriptions.map((s) => ({
      id: s.id || s._id,
      name: s.name,
      type: s.type,
      plan: s.kind === "trial" || s.plan === "Trial"
        ? ta("mockData.plans.trial", { defaultValue: "Trial" })
        : s.plan === "FREEMIUM"
          ? ta("mockData.plans.freemium", { defaultValue: "Freemium" })
          : s.plan,
      trialStartsAt: s.trialStartsAt || null,
      trialEndsAt: s.trialEndsAt || null,
      status: s.status,
      paymentStatus: s.paymentStatus || null,
      startDate: formatDate(s.startDate, i18n.language) || na,
      renewsAt: s.renewsAt ? formatDate(s.renewsAt, i18n.language) : na,
      trialNote: s.trialStartsAt && s.trialEndsAt
        ? ta("includedTrial", {
          from: formatDate(s.trialStartsAt, i18n.language),
          to: formatDate(s.trialEndsAt, i18n.language),
        })
        : null,
    }));
  }, [subscriptions, emptyRow, ta, i18n.language, na]);

  const paymentRows = useMemo(() => {
    if (!payments.length) return emptyRow;
    return payments.map((p) => ({
      id: p.id || p._id,
      dueDate: formatDate(p.dueDate, i18n.language) || na,
      subscription: p.subscription,
      amountValue: Number(p.amount || 0),
      currency: p.currency || "SAR",
      status: p.status,
      checkoutUrl: p.checkoutUrl || null,
      _raw: p,
    }));
  }, [payments, emptyRow, i18n.language, na]);

  const emptyInvoicesMsg = ta("empty.invoices");
  const invoiceRows = useMemo(() => {
    if (!invoices.length) return emptyRow;
    return invoices.map((invoice) => ({
      id: invoice.id,
      number: invoice.number || invoice.id,
      issuedAt: formatDate(invoice.issuedAt, i18n.language) || na,
      amountValue: Number(invoice.amountSar || 0),
      kind: invoice.kind,
      status: invoice.status,
      viewUrl: invoice.viewUrl,
    }));
  }, [invoices, emptyRow, i18n.language, na]);

  const subscriptionColumns = useMemo(
    () => [
      {
        id: "name",
        header: ta("columns.subscription"),
        strong: true,
        render: (row) =>
          row._empty ? (
            <span className="my-account-table-empty-msg">{emptySubscriptionsMsg}</span>
          ) : (
            row.name
          ),
      },
      {
        id: "type",
        header: ta("columns.type"),
        render: (row) => (row._empty ? null : row.type),
      },
      {
        id: "plan",
        header: ta("columns.plan"),
        render: (row) => (row._empty ? null : (
          <span className="my-account-plan">
            <span>{row.plan}</span>
            {row.trialNote ? <span className="my-account-plan-trial">{row.trialNote}</span> : null}
          </span>
        )),
      },
      {
        id: "status",
        header: ta("columns.status"),
        render: (row) =>
          row._empty ? null : (
            <span className={subscriptionStatusClass(row.status)}>
              {localizeSubscriptionStatus(row.status, ta)}
            </span>
          ),
      },
      {
        id: "paymentStatus",
        header: ta("columns.paymentStatus"),
        render: (row) =>
          row._empty ? null : row.paymentStatus ? (
            <span className={paymentStatusClass(row.paymentStatus)}>
              {localizePaymentStatus(row.paymentStatus, ta)}
            </span>
          ) : (
            na
          ),
      },
      {
        id: "startDate",
        header: ta("columns.startDate", { defaultValue: "Starts at" }),
        render: (row) => (row._empty ? null : row.startDate),
      },
      {
        id: "renewsAt",
        header: ta("columns.renews"),
        render: (row) => (row._empty ? null : row.renewsAt),
      },
    ],
    [ta, emptySubscriptionsMsg, na]
  );

  const paymentColumns = useMemo(
    () => [
      {
        id: "dueDate",
        header: ta("columns.dueDate"),
        strong: true,
        render: (row) =>
          row._empty ? (
            <span className="my-account-table-empty-msg">{emptyPaymentsMsg}</span>
          ) : (
            <strong>{row.dueDate}</strong>
          ),
      },
      {
        id: "subscription",
        header: ta("columns.subscription"),
        render: (row) => (row._empty ? null : row.subscription),
      },
      {
        id: "amount",
        header: ta("columns.amount"),
        render: (row) => {
          if (row._empty) return null;
          return (
            <span className="my-account-amount">
              <SARIcon
                size={14}
                color="#030f1b"
                className="my-account-amount__symbol"
              />
              {Number(row.amountValue || 0).toLocaleString(i18n.language || undefined)}
            </span>
          );
        },
      },
      {
        id: "status",
        header: ta("columns.status"),
        render: (row) =>
          row._empty ? null : (
            <span className={paymentStatusClass(row.status)}>
              {localizePaymentStatus(row.status, ta)}
            </span>
          ),
      },
      {
        id: "action",
        header: ta("columns.action"),
        render: (row) => {
          if (row._empty) return null;
          const status = String(row.status || "").toLowerCase();
          const payable = status === "upcoming" || status === "pending" || status === "overdue";
          if (canPerformSubscription && payable && row.checkoutUrl) {
            return (
              <button
                type="button"
                className="my-account-invoice-link"
                onClick={() => window.location.assign(row.checkoutUrl)}
              >
                {ta("continuePayment", { defaultValue: "Continue payment" })}
              </button>
            );
          }
          const isPaid = status === "paid";
          if (!isPaid) return null;
          return (
            <button
              type="button"
              className="my-account-invoice-link"
              disabled={downloadingId === row.id}
              onClick={() => handleDownloadInvoice(row.id)}
            >
              <Download size={14} strokeWidth={2} aria-hidden />
              {ta("downloadInvoice")}
            </button>
          );
        },
      },
    ],
    [ta, emptyPaymentsMsg, downloadingId, i18n.language, handleDownloadInvoice, canPerformSubscription]
  );

  const invoiceColumns = useMemo(
    () => [
      {
        id: "number",
        header: ta("columns.invoice"),
        strong: true,
        render: (row) =>
          row._empty ? (
            <span className="my-account-table-empty-msg">{emptyInvoicesMsg}</span>
          ) : (
            <strong>{row.number}</strong>
          ),
      },
      {
        id: "issuedAt",
        header: ta("columns.issuedAt"),
        render: (row) => (row._empty ? null : row.issuedAt),
      },
      {
        id: "amount",
        header: ta("columns.amount"),
        render: (row) => {
          if (row._empty) return null;
          return (
            <span className="my-account-amount">
              <SARIcon size={14} color="#030f1b" className="my-account-amount__symbol" />
              {Number(row.amountValue || 0).toLocaleString(i18n.language || undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          );
        },
      },
      {
        id: "kind",
        header: ta("columns.kind"),
        render: (row) => (row._empty ? null : ta(`invoiceKind.${row.kind}`, { defaultValue: row.kind })),
      },
      {
        id: "status",
        header: ta("columns.status"),
        render: (row) => (row._empty ? null : ta(`invoiceStatus.${row.status}`, { defaultValue: row.status })),
      },
      {
        id: "action",
        header: ta("columns.action"),
        render: (row) => {
          if (row._empty || !row.viewUrl) return null;
          return (
            <a className="my-account-invoice-link" href={row.viewUrl} target="_blank" rel="noreferrer">
              {ta("viewInvoice")}
            </a>
          );
        },
      },
    ],
    [ta, emptyInvoicesMsg, i18n.language]
  );

  const ticketColumns = useMemo(
    () => [
      {
        id: "id",
        header: ta("columns.ticket"),
        strong: true,
        render: (row) => {
          if (row._viewAll) {
            return (
              <a
                href="#!"
                className="my-account-view-all-link"
                onClick={(e) => {
                  e.preventDefault();
                  toast.info(ta("comingSoon"));
                }}
              >
                {ta("viewAllTickets")}
              </a>
            );
          }
          if (row._empty) {
            return (
              <span className="my-account-table-empty-msg">{emptyTicketsMsg}</span>
            );
          }
          return null;
        },
      },
      { id: "subject", header: ta("columns.subject"), render: () => null },
      { id: "status", header: ta("columns.status"), render: () => null },
      { id: "lastUpdateAt", header: ta("columns.lastUpdate"), render: () => null },
      {
        id: "action",
        header: ta("columns.action"),
        align: "center",
        render: () => null,
      },
    ],
    [ta, emptyTicketsMsg]
  );

  const ticketRows = useMemo(
    () => [
      { id: "empty", _empty: true },
      { id: "view-all", _viewAll: true },
    ],
    []
  );

  const activeSubCount = subscriptions.length;
  const paymentStatusLabel =
    unpaidCount > 0
      ? ta("kpi.dueCount", { count: unpaidCount })
      : activeSubCount > 0
        ? ta("kpi.allPaid")
        : ta("empty.payments");

  if (loading) {
    return (
      <div
        className="my-account-page-root oh-page"
        dir={i18n.dir()}
        lang={i18n.language}
      >
        <MDBContainer fluid className="content-area flex-grow-1">
          <div className="d-flex justify-content-center py-5">
            <MDBSpinner role="status" />
          </div>
        </MDBContainer>
      </div>
    );
  }

  return (
    <div
      className="my-account-page-root oh-page"
      dir={i18n.dir()}
      lang={i18n.language}
    >
      <MDBContainer fluid className="content-area flex-grow-1">
        <div className="oh-page-header">
          <h1 className="page-title-header mb-0">{ta("title")}</h1>
          {subtitle ? <p className="my-account-subtitle mb-0">{subtitle}</p> : null}
        </div>

        <div className="oh-page-section">
          <div className="my-account-kpi-row">
            {canViewSubscriptions ? (
              <AccountKpiCard
                title={ta("kpi.activeSubscriptions")}
                value={String(activeSubCount)}
              />
            ) : null}
            {canViewSubscriptions ? <AccountKpiCard title={ta("kpi.nextRenewal")} value={na} /> : null}
            <AccountKpiCard
              title={ta("kpi.openTickets")}
              value={ta("empty.tickets")}
            />
            {canViewSubscriptions ? (
              <AccountKpiCard
                title={ta("kpi.paymentStatus")}
                value={paymentStatusLabel}
                variant={unpaidCount > 0 ? "warning" : "default"}
              />
            ) : null}
          </div>
        </div>

        <div className="oh-page-section">
          <AccountSection title={ta("sections.businessInfo")} variant="plain">
            <BusinessProfileForm
              profile={summary?.businessProfile}
              businessInfo={summary?.businessInfo}
              profileExists={
                summary?.businessInfo?.crNumber != null
                || (summary?.businessProfile?.profileStatus
                  && summary.businessProfile.profileStatus !== "unsubmitted")
              }
              onSubmitted={handleProfileSubmitted}
              onTaxSaved={handleTaxDetailsSaved}
            />
          </AccountSection>
        </div>

        {canViewSubscriptions ? (
        <div className="oh-page-section">
          <AccountSection title={ta("sections.activeSubscriptions")} variant={bundleItems.length ? "card" : "table"}>
            {bundleItems.length ? (
              <div className="acct-sub-grid">
                {bundleItems.map((item) => (
                  <article key={item.itemId || item.planCode} className="acct-sub-card">
                    <strong>{item.planName || item.planCode}</strong>
                    <span>{item.quantity} × {item.unitLabel || "branch"}</span>
                    <span className={subscriptionStatusClass(bundleMeta?.status)}>{bundleMeta?.status}</span>
                  </article>
                ))}
                {bundleMeta?.scheduledChange?.effectiveAt ? (
                  <p className="acct-sub-pending">
                    {ta("upcomingPlan", {
                      defaultValue: "Upcoming plan: {{items}} from {{date}}",
                      date: formatDate(bundleMeta.scheduledChange.effectiveAt, i18n.language),
                      items: (bundleMeta.scheduledChange.items || []).map((item) => `${item.planName || item.planCode} × ${item.quantity}`).join(", "),
                    })}
                  </p>
                ) : null}
              </div>
            ) : (
              <DataTable
                tableId="my-account-subscriptions"
                columns={subscriptionColumns}
                data={subscriptionRows}
                rowKey="id"
                getRowClassName={(row) =>
                  row._empty ? "my-account-table-empty-row" : ""
                }
                {...tableProps}
              />
            )}
          </AccountSection>
        </div>
        ) : null}

        {canViewSubscriptions ? (
        <div className="oh-page-section">
          <AccountSection title={ta("sections.payments")} variant="table">
            <DataTable
              tableId="my-account-payments"
              columns={paymentColumns}
              data={paymentRows}
              rowKey="id"
              getRowClassName={(row) =>
                row._empty ? "my-account-table-empty-row" : ""
              }
              {...tableProps}
            />
          </AccountSection>
        </div>
        ) : null}

        {canViewSubscriptions ? (
        <div className="oh-page-section">
          <AccountSection title={ta("sections.invoices")} variant="table">
            <DataTable
              tableId="my-account-invoices"
              columns={invoiceColumns}
              data={invoiceRows}
              rowKey="id"
              getRowClassName={(row) =>
                row._empty ? "my-account-table-empty-row" : ""
              }
              {...tableProps}
            />
          </AccountSection>
        </div>
        ) : null}

        <div className="oh-page-section">
          <AccountSection title={ta("sections.openTickets")} variant="table">
            <DataTable
              tableId="my-account-tickets"
              columns={ticketColumns}
              data={ticketRows}
              rowKey="id"
              getRowClassName={(row) =>
                row._viewAll
                  ? "my-account-tickets-view-all-row"
                  : row._empty
                    ? "my-account-table-empty-row"
                    : ""
              }
              {...tableProps}
            />
          </AccountSection>
        </div>
      </MDBContainer>
    </div>
  );
}

export default MyAccountPage;
